import { useCallback } from 'react'
import { CancelToken } from 'axios'
import { ApiAccess } from 'features/session/types'
import {
  ApiGetWithToken,
	ApiDeleteWithToken,
	ApiPatchWithToken,
	ApiPostWithToken,
	useApiFetch,
	useApiPost
} from 'shared/api'
import {
	dashBoardProfileApi,
  dashBoardProfileApiBy,
} from 'interface/endpoints'
import {
	DashBoardProfileResult,
	DashBoardProfileResultList,
	DashBoardProfileFormData
} from './types'

export * from './types'

export const useListDashBoardProfile = (
	profileKey?: string | null,
	searchId?: number | null
) => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
			if (!profileKey || !searchId) return null
      return ApiGetWithToken<DashBoardProfileResultList>(
        apiAccess,
        dashBoardProfileApi(profileKey, `/list/${searchId}`),
        cancelToken
      )
    },
		[profileKey, searchId]
	)
)

export const GetDashBoardProfile = (
	profileKey: string,
	profileId: number,
	apiAccess: ApiAccess,
	cancelToken?: CancelToken
) => ApiGetWithToken<DashBoardProfileResult>(
	apiAccess,
	dashBoardProfileApiBy(profileKey, profileId),
	cancelToken
)
export const useGetDashBoardProfile = (
	profileKey?: string | null,
	profileId?: number | null
) => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
			if (!profileKey || !profileId) return null
      return GetDashBoardProfile(profileKey, profileId, apiAccess, cancelToken)
    },
		[profileKey, profileId]
	)
)

export const PostDashBoardProfile = (
  apiAccess: ApiAccess,
  profileKey: string,
  profileData: DashBoardProfileFormData,
  cancelToken?: CancelToken,
	webSocketId?: string
) => ApiPostWithToken<DashBoardProfileResult>(
	apiAccess,
	dashBoardProfileApi(profileKey),
	profileData,
	cancelToken,
	webSocketId
)
export const usePostDashBoardProfile = (
	profileKey: string,
  callback?: {
    onPosted?: (result: DashBoardProfileResult) => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError?: (reason: any) => void
  }
) => useApiPost<DashBoardProfileFormData, DashBoardProfileResult>(
	useCallback(
		(apiAccess, cancelToken, profileData, webSocketId) => {
			return PostDashBoardProfile(
				apiAccess,
				profileKey,
				profileData,
				cancelToken,
				webSocketId
			)
		},
		[profileKey]
	),
	useCallback(
		(result) => {
			if (callback?.onPosted) callback.onPosted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const EditDashBoardProfile = (
  apiAccess: ApiAccess,
  profileKey: string,
	profileId: number,
  profileData: DashBoardProfileFormData,
  cancelToken?: CancelToken,
	webSocketId?: string
) => ApiPatchWithToken<DashBoardProfileResult>(
	apiAccess,
	dashBoardProfileApiBy(profileKey, profileId),
	profileData,
	cancelToken,
	webSocketId
)
export const useEditDashBoardProfile = (
  profileKey: string,
	profileId: number,
	callback?: {
    onEditted?: (result: DashBoardProfileResult) => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError?: (reason: any) => void
  }
) => useApiPost<DashBoardProfileFormData, DashBoardProfileResult>(
	useCallback(
		(apiAccess, cancelToken, profileToData, webSocketId) => {
			return EditDashBoardProfile(
				apiAccess, profileKey, profileId, profileToData,
				cancelToken, webSocketId
			)
		},
		[profileKey, profileId]
	),
	useCallback(
		(result) => {
			if (callback?.onEditted) callback.onEditted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useDeleteDashBoardProfile = (
  profileKey: string, 
	callback?: {
    onDeleted?: (profileId: number) => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError?: (reason: any) => void
  }
) => useApiPost<number, number>(
	useCallback(
		(apiAccess, cancelToken, profileId, webSocketId) => {
			return ApiDeleteWithToken(
				apiAccess,
        dashBoardProfileApiBy(profileKey, profileId),
				cancelToken,
				webSocketId
			)
		},
		[profileKey]
	),
	useCallback(
		(result) => {
			if (callback?.onDeleted) callback.onDeleted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

